<template>
  <v-container fluid class="pa-6">
    <!-- Header Section with Date Filter -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="filter-card elevation-0" outlined>
          <v-card-text class="pa-4">
            <v-row align="center">
              <v-col cols="12" md="6">
                <h2 class="section-title mb-2">Transacciones de Caja</h2>
                <p class="text-caption grey--text">
                  Todos los movimientos de caja desde la tabla de documentos
                </p>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="issueDate"
                  label="Fecha del reporte"
                  outlined
                  dense
                  type="date"
                  :rules="f_required"
                  prepend-inner-icon="mdi-calendar"
                  @change="get_report"
                  hide-details="auto"
                  :loading="loading_status"
                >
                  <template v-slot:append-outer>
                    <v-btn
                      icon
                      color="primary"
                      @click="get_report"
                      :loading="loading_status"
                    >
                      <v-icon>mdi-magnify</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- KPI Cards Section -->
    <v-row class="mb-4 mb-md-6">
      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined height="100%">
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="success">mdi-cash-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Venta Total</div>
              <div class="kpi-value">{{ formatCurrency(totalSales) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small :color="salesTrend.color">{{ salesTrend.icon }}</v-icon>
                {{ salesTrend.text }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined height="100%">
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="primary">mdi-receipt</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Transacciones</div>
              <div class="kpi-value">{{ totalTransactions }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small color="info">mdi-file-document-multiple</v-icon>
                Documentos emitidos
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined height="100%">
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="warning">mdi-cart</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Ticket Promedio</div>
              <div class="kpi-value">{{ formatCurrency(averageTicket) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">Por transacción</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined height="100%">
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="green">mdi-cash-plus</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Total Ingresos</div>
              <div class="kpi-value">{{ formatCurrency(totalCashIn) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">Movimientos a caja</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined height="100%">
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="red">mdi-cash-minus</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Total Egresos</div>
              <div class="kpi-value">{{ formatCurrency(totalCashOut) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">Movimientos de caja</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Cash Flow Summary -->
    <v-row class="mb-4">
      <v-col cols="12" lg="6">
        <v-card class="summary-card elevation-0" outlined>
          <v-card-title class="pa-0">
            <v-toolbar flat class="minimal-toolbar">
              <v-icon class="mr-2" color="blue">mdi-scale-balance</v-icon>
              <h3 class="card-title">Conciliación de Caja</h3>
            </v-toolbar>
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="6">
                <div class="text-h6 font-weight-medium mb-2">Ventas + Ingresos</div>
                <div class="text-h5 success--text font-weight-bold">
                  +{{ formatCurrency(totalSales + totalCashIn) }}
                </div>
              </v-col>
              <v-col cols="6">
                <div class="text-h6 font-weight-medium mb-2">Egresos</div>
                <div class="text-h5 error--text font-weight-bold">
                  -{{ formatCurrency(totalCashOut) }}
                </div>
              </v-col>
            </v-row>
            <v-divider class="my-4"></v-divider>
            <v-row>
              <v-col cols="12">
                <div class="text-h6 font-weight-medium mb-2">Flujo de Caja Neto</div>
                <div
                  class="text-h4 font-weight-bold"
                  :class="netCashFlow >= 0 ? 'success--text' : 'error--text'"
                >
                  {{ formatCurrency(netCashFlow) }}
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="6">
        <v-card class="summary-card elevation-0" outlined>
          <v-card-title class="pa-0">
            <v-toolbar flat class="minimal-toolbar">
              <v-icon class="mr-2" color="warning">mdi-credit-card</v-icon>
              <h3 class="card-title">Método Principal de Pago</h3>
            </v-toolbar>
          </v-card-title>
          <v-card-text class="pa-4">
            <div class="text-h5 font-weight-bold">{{ topPaymentMethod.name }}</div>
            <div class="text-h6 grey--text">
              {{ topPaymentMethod.percentage }}% del total
            </div>
            <div class="mt-3">
              <v-progress-linear
                :value="topPaymentMethod.percentage"
                color="primary"
                height="8"
                rounded
              ></v-progress-linear>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Summary Sections -->
    <v-row class="mb-4">
      <!-- Document Types Summary -->
      <v-col cols="12" lg="6">
        <v-row>
          <v-col cols="12">
            <v-card class="summary-card elevation-0" outlined>
              <v-card-title class="pa-0">
                <v-toolbar flat class="minimal-toolbar">
                  <v-icon class="mr-2" color="success">mdi-file-document-multiple</v-icon>
                  <h3 class="card-title">Por Tipo de Documento</h3>
                </v-toolbar>
              </v-card-title>

              <v-data-table
                :headers="headersDocType"
                :items="docType"
                :items-per-page="5"
                hide-default-footer
                class="minimal-table"
                :loading="loading_status"
                dense
              >
                <template v-slot:[`item.label`]="{ item }">
                  <v-chip small :color="getDocColor(item.label)" dark>
                    {{ getDocLabel(item.label) }}
                  </v-chip>
                </template>

                <template v-slot:[`item.qty`]="{ item }">
                  <span class="font-weight-medium">{{ item.qty }}</span>
                </template>

                <template v-slot:[`item.value`]="{ item }">
                  <span class="font-weight-medium">{{ formatCurrency(item.value) }}</span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="summary-card elevation-0" outlined>
              <v-card-title class="pa-0">
                <v-toolbar flat class="minimal-toolbar">
                  <v-icon class="mr-2" color="info">mdi-account-cash</v-icon>
                  <h3 class="card-title">Por Vendedor</h3>
                </v-toolbar>
              </v-card-title>

              <v-data-table
                :headers="headersDocType"
                :items="Vendors"
                :items-per-page="5"
                hide-default-footer
                class="minimal-table"
                :loading="loading_status"
                dense
              >
                <template v-slot:[`item.label`]="{ item }">
                  <div class="vendor-info">
                    <v-avatar size="32" color="primary" class="mr-2">
                      <span class="white--text text-caption">
                        {{ getInitials(item.label) }}
                      </span>
                    </v-avatar>
                    <span class="text-truncate">{{ item.label }}</span>
                  </div>
                </template>

                <template v-slot:[`item.qty`]="{ item }">
                  <span class="font-weight-medium">{{ item.qty }}</span>
                </template>

                <template v-slot:[`item.value`]="{ item }">
                  <span class="font-weight-medium">{{ formatCurrency(item.value) }}</span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <!-- Payment Methods Summary -->
      <v-col cols="12" lg="6">
        <v-card class="summary-card elevation-0" outlined>
          <v-card-title class="pa-0">
            <v-toolbar flat class="minimal-toolbar">
              <v-icon class="mr-2" color="primary">mdi-cash-register</v-icon>
              <h3 class="card-title">Formas de Pago</h3>
              <v-spacer></v-spacer>
              <v-chip small label> {{ cash_type.length }} tipos </v-chip>
            </v-toolbar>
          </v-card-title>

          <v-data-table
            :headers="headers_cash"
            :items="cash_type"
            :items-per-page="5"
            hide-default-footer
            class="minimal-table"
            :loading="loading_status"
            loading-text="Cargando..."
            dense
          >
            <template v-slot:[`item.label`]="{ item }">
              <div class="payment-method">
                <v-icon small class="mr-2" :color="getPaymentColor(item.label)">
                  {{ getPaymentIcon(item.label) }}
                </v-icon>
                {{ item.label }}
              </div>
            </template>

            <template v-slot:[`item.qty`]="{ item }">
              <v-chip x-small label>{{ item.qty }}</v-chip>
            </template>

            <template v-slot:[`item.value`]="{ item }">
              <div class="text-right">
                <div class="font-weight-medium">{{ formatCurrency(item.value) }}</div>
                <div class="text-caption grey--text">
                  {{ getPercentage(item.value) }}%
                </div>
              </div>
            </template>

            <template v-slot:body.append>
              <tr class="grey lighten-4 font-weight-bold">
                <td>TOTAL</td>
                <td class="text-center">{{ sumTable("cash_type", "qty") }}</td>
                <td class="text-right">{{ formatCurrency(totalSales) }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Transactions Table -->
    <v-card class="main-card elevation-0" outlined>
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title" :class="{ 'text-h6': $vuetify.breakpoint.mobile }">
            Movimientos de Caja - {{ formatDate(issueDate) }}
          </h2>
          <v-spacer></v-spacer>
          <v-chip small color="primary" class="mr-2">
            {{ allTransactions.length }} transacciones
          </v-chip>
          <v-btn icon color="primary" @click="openPrintDrawer" class="d-none d-sm-flex">
            <v-icon>mdi-printer</v-icon>
          </v-btn>
          <v-btn icon color="success" @click="downloadExcel" class="d-none d-sm-flex">
            <v-icon>mdi-microsoft-excel</v-icon>
          </v-btn>
        </v-toolbar>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Desktop Table -->
      <template v-if="$vuetify.breakpoint.mdAndUp">
        <v-data-table
          :headers="headers_transactions"
          :items="allTransactions"
          :items-per-page="50"
          hide-default-footer
          class="minimal-table"
          :loading="loading_status"
          dense
        >
          <template v-slot:[`item.datetime`]="{ item }">
            <div class="text-caption">
              <div class="font-weight-medium">{{ formatDate(item.date) }}</div>
              <div class="grey--text">{{ formatTime(item.time) }}</div>
            </div>
          </template>

          <template v-slot:[`item.type`]="{ item }">
            <v-chip
              x-small
              :color="item.type === 'income' ? 'success' : 'error'"
              dark
              label
            >
              {{ item.type === "income" ? "Ingreso" : "Egreso" }}
            </v-chip>
          </template>

          <template v-slot:[`item.payment_method`]="{ item }">
            <div class="payment-method">
              <v-icon small class="mr-1" :color="getPaymentColor(item.payment_method)">
                {{ getPaymentIcon(item.payment_method) }}
              </v-icon>
              {{ item.payment_method }}
            </div>
          </template>

          <template v-slot:[`item.concept`]="{ item }">
            <div class="concept-cell">
              <div class="font-weight-medium">{{ item.concept }}</div>
              <div class="text-caption grey--text" v-if="item.reference">
                Ref: {{ item.reference }}
              </div>
              <div class="text-caption grey--text" v-if="item.beneficiary">
                A: {{ item.beneficiary }}
              </div>
            </div>
          </template>

          <template v-slot:[`item.amount`]="{ item }">
            <span
              class="font-weight-medium"
              :class="item.amount >= 0 ? 'success--text' : 'error--text'"
            >
              {{ formatCurrency(item.amount) }}
            </span>
          </template>
        </v-data-table>
      </template>

      <!-- Mobile List -->
      <template v-else>
        <v-list v-if="allTransactions.length > 0" class="py-0">
          <div v-for="(item, index) in allTransactions" :key="`transaction-${index}`">
            <v-list-item class="px-4 py-3">
              <v-list-item-content>
                <div class="d-flex justify-space-between align-start mb-2">
                  <div class="text-caption grey--text">
                    {{ formatDate(item.date) }} {{ formatTime(item.time) }}
                  </div>
                  <v-chip
                    x-small
                    :color="item.type === 'income' ? 'success' : 'error'"
                    dark
                    label
                  >
                    {{ item.type === "income" ? "Ingreso" : "Egreso" }}
                  </v-chip>
                </div>

                <div class="d-flex align-center mb-2">
                  <v-icon
                    small
                    class="mr-1"
                    :color="getPaymentColor(item.payment_method)"
                  >
                    {{ getPaymentIcon(item.payment_method) }}
                  </v-icon>
                  <span class="text-body-2">{{ item.payment_method }}</span>
                </div>

                <div class="mb-2">
                  <div class="font-weight-medium">{{ item.concept }}</div>
                  <div class="text-caption grey--text" v-if="item.reference">
                    Ref: {{ item.reference }}
                  </div>
                  <div class="text-caption grey--text" v-if="item.beneficiary">
                    A: {{ item.beneficiary }}
                  </div>
                </div>

                <div class="text-right">
                  <div
                    class="font-weight-bold text-h6"
                    :class="item.amount >= 0 ? 'success--text' : 'error--text'"
                  >
                    {{ formatCurrency(item.amount) }}
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>
            <v-divider v-if="index < allTransactions.length - 1" />
          </div>
        </v-list>

        <!-- Empty State Mobile -->
        <div v-else-if="!loading_status" class="text-center py-12">
          <v-icon size="64" color="grey lighten-2">mdi-cash-register</v-icon>
          <h3 class="text-h6 mt-4 grey--text">No hay transacciones</h3>
          <p class="grey--text mt-2">
            No se encontraron movimientos de caja para esta fecha
          </p>
        </div>
      </template>
    </v-card>

    <!-- Document Viewer Dialog -->
    <docViewer :item="item" :dialog="dialog" @close="dialog = false" @refresh="refresh" />

    <!-- Cash Report Printer -->
    <CashReportPrinter
      :drawer.sync="printDrawer"
      :report-data="printReportData"
      @printed="onReportPrinted"
      @print-error="onPrintError"
    />

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday } from "@/services/webserver.js";
import { getDocumentTypeName } from "@/utils/documentTypes.js";
import docViewer from "@/components/doc_viewer.vue";
import CashReportPrinter from "@/components/CashReportPrinter.vue";
import createDoc from "@/utils/create_doc.js";
import * as XLSX from "xlsx";

export default {
  name: "CashFlowReport",
  components: { docViewer, CashReportPrinter },
  data() {
    return {
      // Validation
      f_required: [(v) => !!v || "Campo requerido"],

      // Loading states
      loading_status: false,

      // Date
      issueDate: getToday(),

      // Data
      cash_data: [],
      cash_type: [],
      docType: [],
      Vendors: [],
      cin: [], // Ingresos a caja
      cout: [], // Egresos de caja
      allTransactions: [], // Todas las transacciones ordenadas por fecha/hora

      // Dialog
      dialog: false,
      item: createDoc(),

      // Print Drawer
      printDrawer: false,

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // Headers
      headers_cash: [
        {
          text: "Forma de pago",
          value: "label",
          sortable: false,
          width: "50%",
        },
        {
          text: "Cant.",
          value: "qty",
          align: "center",
          width: "20%",
        },
        {
          text: "Valor",
          value: "value",
          align: "right",
          width: "30%",
        },
      ],

      headersDocType: [
        {
          text: "Tipo",
          value: "label",
          sortable: false,
          width: "50%",
        },
        {
          text: "Cant.",
          value: "qty",
          align: "center",
          width: "20%",
        },
        {
          text: "Valor",
          value: "value",
          align: "right",
          width: "30%",
        },
      ],

      headers_transactions: [
        {
          text: "Fecha/Hora",
          value: "datetime",
          sortable: false,
          width: "20%",
        },
        {
          text: "Tipo",
          value: "type",
          sortable: false,
          width: "10%",
        },
        {
          text: "Forma de Pago",
          value: "payment_method",
          sortable: false,
          width: "20%",
        },
        {
          text: "Concepto",
          value: "concept",
          sortable: false,
          width: "35%",
        },
        {
          text: "Monto",
          value: "amount",
          align: "right",
          width: "15%",
        },
      ],
    };
  },

  computed: {
    // KPI Calculations
    totalSales() {
      return this.cash_type.reduce((sum, item) => sum + parseFloat(item.value || 0), 0);
    },

    totalTransactions() {
      return this.allTransactions.length;
    },

    averageTicket() {
      if (this.totalTransactions === 0) return 0;
      return this.totalSales / this.totalTransactions;
    },

    topPaymentMethod() {
      if (this.cash_type.length === 0) {
        return { name: "-", percentage: 0 };
      }
      const top = this.cash_type[0];
      const percentage =
        this.totalSales > 0 ? Math.round((top.value / this.totalSales) * 100) : 0;
      return {
        name: top.label,
        percentage,
      };
    },

    salesTrend() {
      // This would compare with previous day in real implementation
      // For now, showing a static trend
      return {
        icon: "mdi-trending-up",
        color: "success",
        text: "vs día anterior",
      };
    },

    // Cash Flow Calculations
    totalCashIn() {
      return this.cin.reduce((sum, item) => sum + parseFloat(item.doc_val || 0), 0);
    },

    totalCashOut() {
      return this.cout.reduce((sum, item) => sum + parseFloat(item.doc_val || 0), 0);
    },

    netCashFlow() {
      return this.totalSales + this.totalCashIn - this.totalCashOut;
    },

    printReportData() {
      return {
        date: this.issueDate,
        totalSales: this.totalSales,
        totalTransactions: this.totalTransactions,
        averageTicket: this.averageTicket,
        totalCashIn: this.totalCashIn,
        totalCashOut: this.totalCashOut,
        netCashFlow: this.netCashFlow,
        paymentMethods: this.cash_type,
        documentTypes: this.docType,
        vendors: this.Vendors,
        cashIn: this.cin,
        cashOut: this.cout,
        documents: this.cash_data,
      };
    },
  },

  mounted() {
    this.get_report();
  },

  methods: {
    // Formatting methods
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("es-CO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    formatTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleTimeString("es-CO", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatCurrency(value) {
      if (!value && value !== 0) return "$0";
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },

    // Utility methods
    getPercentage(value) {
      if (this.totalSales === 0) return 0;
      return Math.round((value / this.totalSales) * 100);
    },

    getInitials(name) {
      if (!name) return "?";
      const parts = name.split(/[@.\s]+/);
      return parts[0].substring(0, 2).toUpperCase();
    },

    getPaymentIcon(payment) {
      const icons = {
        EFECTIVO: "mdi-cash",
        TARJETA: "mdi-credit-card",
        TRANSFERENCIA: "mdi-bank-transfer",
        CREDITO: "mdi-calendar-clock",
        CHEQUE: "mdi-checkbook",
      };
      const key = Object.keys(icons).find((k) => payment.toUpperCase().includes(k));
      return icons[key] || "mdi-cash-multiple";
    },

    getPaymentColor(payment) {
      const colors = {
        EFECTIVO: "success",
        TARJETA: "primary",
        TRANSFERENCIA: "info",
        CREDITO: "warning",
        CHEQUE: "secondary",
      };
      const key = Object.keys(colors).find((k) => payment.toUpperCase().includes(k));
      return colors[key] || "grey";
    },

    getDocLabel(docType) {
      return getDocumentTypeName(docType);
    },

    getDocColor(docType) {
      const colors = {
        TK: "info",
        FS: "success",
        FC: "primary",
        FE: "secondary",
        NC: "warning",
      };
      return colors[docType] || "grey";
    },

    // Message handling
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    // Data methods
    sumTable(table, key) {
      return this[table]
        .reduce((sum, item) => sum + parseFloat(item[key] || 0), 0)
        .toLocaleString("es");
    },

    downloadExcel() {
      try {
        const exportData = this.allTransactions.map((item) => ({
          Fecha: this.formatDate(item.date),
          Hora: this.formatTime(item.time),
          Tipo: item.type === 'income' ? 'Ingreso' : 'Egreso',
          Forma_Pago: item.payment_method,
          Concepto: item.concept,
          Referencia: item.reference || "-",
          Beneficiario: item.beneficiary || "-",
          Monto: item.amount,
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Reporte de Caja");
        XLSX.writeFile(wb, `reporte_caja_${this.issueDate}.xlsx`);
        this.showMessage("Archivo Excel descargado", "success");
      } catch (error) {
        this.showMessage("Error al descargar Excel", "error");
      }
    },

    // API methods
    async get_report() {
      this.loading_status = true;

      const qry = {
        store: window.store.store_id,
        date: this.issueDate,
        userId: window.profile.user_email,
      };
      console.log("Query para obtener reporte:", qry);

      try {
        const response = await new Promise((resolve, reject) => {
          webserver("get_cashdrawer_report", qry, resolve, reject);
        });
        console.log("Respuesta del servidor:", response);

        // Verificar si la respuesta contiene error
        if (response && response.error) {
          throw new Error(response.message || "Error en la respuesta del servidor");
        }

        // Procesar los datos de pagos
        this.processPaymentData(response.payments || []);

        // Cargar datos de flujo de caja
        await this.loadCashFlowData();

        this.loading_status = false;
      } catch (error) {
        this.loading_status = false;
        console.error("Error en get_report:", error);
        this.showMessage(`Error al cargar reporte: ${error.message}`, "error");
      }
    },

    processPaymentData(data) {
      // Filtrar datos nulos o inválidos
      const validData = data.filter(
        (payment) =>
          payment &&
          payment.payment_concept &&
          payment.doc_type &&
          payment.userId &&
          payment.payment_value !== null &&
          payment.payment_value !== undefined
      );

      // Process payment methods
      const paymentResult = [];
      const paymentMap = {};

      validData.forEach((payment) => {
        const key = payment.payment_concept || "Sin definir";
        if (!paymentMap[key]) {
          paymentMap[key] = { label: key, qty: 0, value: 0 };
          paymentResult.push(paymentMap[key]);
        }
        paymentMap[key].qty += 1;
        paymentMap[key].value += parseFloat(payment.payment_value || 0);
      });

      // Sort by value descending
      paymentResult.sort((a, b) => b.value - a.value);
      this.cash_type = paymentResult;

      // Process document types
      const docResult = [];
      const docMap = {};

      validData.forEach((payment) => {
        const key = payment.doc_type || "Sin definir";
        if (!docMap[key]) {
          docMap[key] = { label: key, qty: 0, value: 0 };
          docResult.push(docMap[key]);
        }
        docMap[key].qty += 1;
        docMap[key].value += parseFloat(payment.payment_value || 0);
      });

      this.docType = docResult;

      // Process vendors
      const vendorResult = [];
      const vendorMap = {};

      validData.forEach((payment) => {
        const key = payment.userId || "Usuario desconocido";
        if (!vendorMap[key]) {
          vendorMap[key] = { label: key, qty: 0, value: 0 };
          vendorResult.push(vendorMap[key]);
        }
        vendorMap[key].qty += 1;
        vendorMap[key].value += parseFloat(payment.payment_value || 0);
      });

      vendorResult.sort((a, b) => b.value - a.value);
      this.Vendors = vendorResult;
    },

    async loadCashFlowData() {
      this.loading_status = true;

      const qry = {
        store: window.store.store_id,
        table: "docs",
        filters: [
          {
            field: "doc_type",
            operator: " IN ",
            value: "'CS-IN','CS-OUT','TK','FS','FC','FE','NC','ND','GASTOS'",
          },
          {
            field: "DATE(issuedate)",
            operator: "=",
            value: this.issueDate,
          },
          {
            field: "store",
            operator: "=",
            value: window.store.store_id,
          },
        ],
      };

      // Filtrar por usuario si es usuario normal
      if (this.$store.getters.profile.user_access === "USUARIO") {
        qry.filters.push({
          field: "userId",
          operator: "=",
          value: this.$store.state.profile.email,
        });
      }

      try {
        const data = await new Promise((resolve, reject) => {
          webserver("get_dataTable", qry, resolve, reject);
        });

        // Procesar los datos unificados
        this.processUnifiedCashData(data || []);
        this.loading_status = false;
      } catch (error) {
        console.error("Error al cargar datos de flujo de caja:", error);
        this.showMessage("Error al cargar datos de flujo de caja", "error");
        this.loading_status = false;
      }
    },

    processUnifiedCashData(data) {
      const transactions = [];

      data.forEach((item) => {
        // Determinar si es ingreso o egreso basado en el tipo de documento
        const isIncome = this.isIncomeDocument(item.doc_type);
        const amount = isIncome ? item.doc_value : -Math.abs(item.doc_value);

        transactions.push({
          id: item.id || Math.random(),
          date: item.issuedate ? item.issuedate.split(" ")[0] : this.issueDate,
          time: item.issuedate ? item.issuedate.split(" ")[1] || "00:00:00" : "00:00:00",
          datetime: new Date(item.issuedate || `${this.issueDate} 00:00:00`),
          type: isIncome ? "income" : "expense",
          payment_method: this.getPaymentMethodFromDocType(item.doc_type),
          concept: this.getConceptFromDocType(item.doc_type, item),
          amount: amount,
          reference: item.doc_number || "",
          beneficiary: item.PartyName || item.supplier || "N/A",
          doc_type: item.doc_type,
          raw_item: item, // Guardar el item completo para referencia
        });
      });

      // Ordenar por fecha y hora (de más antigua a más reciente)
      transactions.sort((a, b) => a.datetime - b.datetime);

      this.allTransactions = transactions;

      // También actualizar las propiedades cin y cout para compatibilidad
      this.cin = transactions.filter((t) => t.type === "income");
      this.cout = transactions.filter((t) => t.type === "expense");
    },

    isIncomeDocument(docType) {
      // Documentos que representan ingresos
      const incomeTypes = ["CS-IN", "TK", "FS", "FC", "FE"];
      return incomeTypes.includes(docType);
    },

    getPaymentMethodFromDocType(docType) {
      // Mapear tipos de documento a métodos de pago
      const paymentMap = {
        "CS-IN": "Efectivo",
        "CS-OUT": "Efectivo",
        TK: "Efectivo",
        FS: "Efectivo",
        FC: "Crédito",
        FE: "Efectivo",
        NC: "Efectivo",
        ND: "Efectivo",
        GASTOS: "Efectivo",
      };
      return paymentMap[docType] || "Efectivo";
    },

    getConceptFromDocType(docType, item) {
      // Generar concepto descriptivo basado en el tipo de documento
      const conceptMap = {
        "CS-IN": `Ingreso de caja - ${item.doc_number || "N/A"}`,
        "CS-OUT": `Egreso de caja - ${item.doc_number || "N/A"}`,
        TK: `Ticket - ${item.doc_number || "N/A"}`,
        FS: `Factura simplificada - ${item.doc_number || "N/A"}`,
        FC: `Factura de contado - ${item.doc_number || "N/A"}`,
        FE: `Factura electrónica - ${item.doc_number || "N/A"}`,
        NC: `Nota de crédito - ${item.doc_number || "N/A"}`,
        ND: `Nota de débito - ${item.doc_number || "N/A"}`,
        GASTOS: `Gasto - ${item.doc_number || "N/A"}`,
      };
      return conceptMap[docType] || `Documento ${docType} - ${item.doc_number || "N/A"}`;
    },

    get_e_doc(item) {
      const issudate = item.issuedate.split(" ");
      const doc = {
        store: item.store,
        issueDate: issudate[0],
        did: item.doc_number,
      };

      this.loading_status = true;
      webserver(
        "get_invoice_fe",
        doc,
        (data) => {
          this.loading_status = false;
          this.item = data;
          this.dialog = true;
        },
        () => {
          this.loading_status = false;
          this.showMessage("Error al cargar documento", "error");
        }
      );
    },

    refresh() {
      this.get_report();
    },

    openPrintDrawer() {
      this.printDrawer = true;
    },

    onReportPrinted() {
      this.showMessage("Reporte impreso correctamente", "success");
      this.printDrawer = false;
    },

    onPrintError(error) {
      this.showMessage("Error al imprimir: " + error.message, "error");
    },
  },
};
</script>

<style scoped>
/* Filter Card */
.filter-card {
  border: 1px solid #e0e0e0;
}

/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Summary Cards */
.summary-card {
  border: 1px solid #e0e0e0;
  height: 100%;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

/* Data Table */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 12px 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
  transition: background-color 0.2s;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 12px 16px !important;
  font-size: 0.875rem !important;
}

/* Clickable rows */
.clickable-rows >>> tbody tr {
  cursor: pointer;
}

/* Custom cells */
.payment-method {
  display: flex;
  align-items: center;
}

.vendor-info {
  display: flex;
  align-items: center;
  max-width: 200px;
}

.document-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-time {
  display: flex;
  flex-direction: column;
}

.status-badges {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 2px !important;
}

/* Text utilities */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Responsive */
@media (max-width: 600px) {
  .kpi-value {
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 1.125rem;
  }

  .card-title {
    font-size: 1rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
    padding: 16px !important;
  }

  .kpi-icon {
    margin-bottom: 8px;
  }

  .minimal-toolbar {
    padding: 12px 16px !important;
  }
}

@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .kpi-card .v-card__text {
    flex-direction: row;
    text-align: left;
  }
}
</style>
